import React from "react";
import { Square } from "lucide-react";
import _ from "lodash";
import type { GlassData, Material, FirebaseRecord } from "../types";

const rgbToHex = (value: number): string => {
  let hex = Number(value).toString(16);
  if (hex.length < 2) {
    hex = "0" + hex;
  }
  return hex;
};

export const getHexColor = (color?: { r: number; g: number; b: number }): string => {
  if (typeof color === "undefined") {
    return "black";
  }
  const { r, g, b } = color;
  const red = rgbToHex(r);
  const green = rgbToHex(g);
  const blue = rgbToHex(b);
  return `#${red}${green}${blue}`;
};

export interface GlassDropdownOption {
  key: string;
  text: string;
  value: string;
  color?: string;
  content?: React.ReactNode;
}

export const getGlassDataDropdownOptionsFromInventory = (
  inventory: Record<string, { color?: { rgb?: { r: number; g: number; b: number } }; description?: string; type?: string }> | undefined
): GlassDropdownOption[] => {
  if (!inventory) return [{ key: "unknown", text: "No Glass Inventory Found", value: "" }];
  return Object.keys(inventory).map((key) => {
    const { color, description, type } = inventory[key];
    return {
      key,
      text: `${key} - ${description}:${type}`,
      value: key,
      color: color?.rgb && getHexColor(color.rgb),
      content: (
        <>
          {/* Colour goes inline on the svg itself: CommandItem's
              [&_svg:not([class*='text-'])]:text-muted-foreground rule overrides
              any colour inherited from a wrapping element. */}
          <Square
            className="inline-block h-4 w-4 fill-current"
            style={color?.rgb ? { color: getHexColor(color.rgb) } : undefined}
          />
          {`${key} - ${description}: ${type}`}
        </>
      ),
    };
  });
};

export const glasstypes = [
  { label: "Bullseye", value: "Bullseye" },
  { label: "Spectrum", value: "Spectrum" },
  { label: "Wissmach", value: "Wissmach" },
  { label: "Baoili COE 85", value: "Baoli COE 85" },
  { label: "Baoili COE 90", value: "Baoli COE 90" },
  { label: "Bottle", value: "Bottle" },
  { label: "Float", value: "Float" },
];

export const getGlassItem = (glass_ref: string, glass_data: GlassData): React.ReactNode => {
  const color = glass_data?.inventory?.[glass_ref]?.color;
  const colour_name = getGlassReactionTypeColourName(glass_ref, glass_data);
  const characters = getGlassReactionTypeCharacters(glass_ref, glass_data);
  return (
    <>
      <span
        style={{
          color: color?.multicolored ? "black" : getHexColor(color?.rgb),
          fontSize: color?.multicolored ? 18 : 32,
        }}
      >
        {color?.multicolored ? <span>Multicoloured</span> : color && <Square className="inline-block h-5 w-5 fill-current" />}

        {characters && (
          <span
            className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: getGlassReactionTypeColour(glass_ref, glass_data) }}
          >
            {characters}
          </span>
        )}
      </span>
    </>
  );
};

export const getGlassReactionTypeCharacters = (code: string, glass_data: GlassData): string => {
  const code_root = code.split("-")[0];
  const reaction = glass_data?.reactions[code_root] || glass_data?.reactions[code];

  let chars = "";
  switch (reaction?.reaction_type) {
    case "REACTIVE":
      chars = "RE";
      break;
    case "SULFUR/SELENIUM":
    case "SULFUR":
      chars = "S";
      break;
    case "LEAD":
      chars = "Pb";
      break;
    case "COPPER":
      chars = "Cu";
      break;
  }

  if (reaction?.instability_type === "CADMIUM-BEARING") {
    chars = chars ? chars + "+Cd" : "Cd";
  }

  return chars;
};

export const getGlassReactionTypeColour = (code: string, glass_data: GlassData): string => {
  const code_root = code.split("-")[0];
  const reaction = glass_data?.reactions[code_root] || glass_data?.reactions[code];

  if (reaction?.instability_type === "CADMIUM-BEARING") {
    return "var(--kiln-reaction-cadmium)";
  }

  switch (reaction?.reaction_type) {
    case "REACTIVE":
      return "var(--kiln-reaction-reactive)";
    case "SULFUR/SELENIUM":
    case "SULFUR":
      return "var(--kiln-reaction-sulfur)";
    case "LEAD":
      return "var(--kiln-reaction-lead)";
    case "COPPER":
      return "var(--kiln-reaction-copper)";
    default:
      return "#ffffff";
  }
};

export const getGlassReactionTypeColourName = (code: string, glass_data: GlassData): string => {
  const code_root = code.split("-")[0];
  const reaction = glass_data?.reactions[code_root] || glass_data?.reactions[code];

  if (reaction?.instability_type === "CADMIUM-BEARING") {
    return "orange";
  }

  switch (reaction?.reaction_type) {
    case "REACTIVE":
      return "brown";
    case "SULFUR/SELENIUM":
    case "SULFUR":
      return "yellow";
    case "LEAD":
      return "grey";
    case "COPPER":
      return "green";
    default:
      return "white";
  }
};

export const getReactingMaterials = (
  materials: FirebaseRecord<Material & { glass_reference: string }>,
  glass_data: GlassData
): Record<string, Material & { reaction_type?: string }> => {
  const materials_with_reaction_types = _.mapValues(materials, (material) => ({
    ...material,
    reaction_type:
      glass_data?.reactions[material.glass_reference.split("-")[0]]?.reaction_type ||
      glass_data?.reactions[material.glass_reference]?.reaction_type,
  }));
  const all_reaction_types = [
    ...new Set(
      Object.values(materials_with_reaction_types).map((material) => material.reaction_type)
    ),
  ].filter((a): a is string => a != null);

  // Annotate the materials list to contain only the entries that have a reaction within them.
  const reacting_materials = Object.fromEntries(
    Object.entries(materials_with_reaction_types).filter(([_code, material]) =>
      reactsWith(material.reaction_type, all_reaction_types)
    )
  );
  return reacting_materials;
};

const reactsWith = (reaction_type: string | undefined, all_reaction_types: string[]): boolean => {
  switch (reaction_type) {
    case "REACTIVE":
      return all_reaction_types.includes("COPPER");
    case "SULFUR/SELENIUM":
    case "SULFUR":
      return !!(all_reaction_types.includes("COPPER") || all_reaction_types.includes("LEAD"));
    case "LEAD":
      return !!(all_reaction_types.includes("SULFUR/SELENIUM") || all_reaction_types.includes("SULFUR"));
    case "COPPER":
      return !!(
        all_reaction_types.includes("REACTIVE") ||
        all_reaction_types.includes("SULFUR/SELENIUM") ||
        all_reaction_types.includes("SULFUR")
      );
    default:
      return false;
  }
};
